import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { hexAlpha, readableOn } from '../../../components/ui';
import { ROLE_LABEL, NetworkAnalysis } from '../graphAnalytics';
import { AreaSummary, isStale, LOW_CONFIDENCE, networkPercentile } from './influenceInsights';
import { InfluenceArea, InfluenceEntity } from './influenceModel';
import { areaColor, EntityAvatar, relativeTime } from './ui';

export type MatrixSortKey = 'name' | 'average' | 'network' | string;
export interface MatrixSort {
  key: MatrixSortKey;
  dir: 'asc' | 'desc';
}

interface MatrixLensProps {
  entities: InfluenceEntity[];
  areas: InfluenceArea[];
  summaries: AreaSummary[];
  analysis: NetworkAnalysis | null;
  sort: MatrixSort;
  onSort: (sort: MatrixSort) => void;
  query: string;
  matchIds: Set<string> | null;
  selectedId: string | null;
  hoverId: string | null;
  compareIds: string[];
  highlightIds: string[];
  active: boolean;
  /** Phones: narrower columns and room for the bottom dock */
  compact?: boolean;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null) => void;
  onOpen: (id: string) => void;
}

const ROW = 44;
const HEAD = 52;
const OVERSCAN = 8;

export function sortEntities(entities: InfluenceEntity[], sort: MatrixSort, analysis: NetworkAnalysis | null): InfluenceEntity[] {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const value = (e: InfluenceEntity): number | null => {
    if (sort.key === 'average') return e.assessed ? e.average : null;
    if (sort.key === 'network') return networkPercentile(analysis, e.id);
    const s = e.scoreByArea[sort.key];
    return s ? s.score : null;
  };
  return entities.slice().sort((a, b) => {
    if (sort.key === 'name') return dir * a.label.localeCompare(b.label);
    const va = value(a);
    const vb = value(b);
    // Missing values always last, whatever the direction
    if (va === null && vb === null) return a.label.localeCompare(b.label);
    if (va === null) return 1;
    if (vb === null) return -1;
    return dir * (va - vb) || b.average - a.average || a.label.localeCompare(b.label);
  });
}

export const MatrixLens: React.FC<MatrixLensProps> = ({
  entities,
  areas,
  summaries,
  analysis,
  sort,
  onSort,
  query,
  matchIds,
  selectedId,
  hoverId,
  compareIds,
  highlightIds,
  active,
  compact = false,
  onHover,
  onSelect,
  onOpen,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);
  const [focusIndex, setFocusIndex] = useState(0);
  const pendingFocus = useRef(false);
  const rafRef = useRef<number | null>(null);

  const rows = useMemo(() => {
    const visible = query.trim() && matchIds ? entities.filter((e) => matchIds.has(e.id)) : entities;
    return sortEntities(visible, sort, analysis);
  }, [entities, query, matchIds, sort, analysis]);

  const summaryById = useMemo(() => {
    const map = new Map<string, AreaSummary>();
    summaries.forEach((s) => map.set(s.area.id, s));
    return map;
  }, [summaries]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewport(el.clientHeight || 600);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onScroll = () => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setScrollTop(scrollRef.current?.scrollTop || 0);
    });
  };

  // Bring the selected row into view when selection comes from elsewhere
  useEffect(() => {
    if (!active || !selectedId) return;
    const index = rows.findIndex((r) => r.id === selectedId);
    const el = scrollRef.current;
    if (index === -1 || !el) return;
    const top = index * ROW;
    if (top < el.scrollTop || top + ROW > el.scrollTop + el.clientHeight - HEAD) {
      el.scrollTo?.({ top: Math.max(0, top - el.clientHeight / 3), behavior: 'smooth' });
    }
    setFocusIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, active]);

  const start = Math.max(0, Math.floor(scrollTop / ROW) - OVERSCAN);
  const end = Math.min(rows.length, Math.ceil((scrollTop + viewport) / ROW) + OVERSCAN);
  const slice = rows.slice(start, end);

  const moveFocus = useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(rows.length - 1, index));
      setFocusIndex(next);
      pendingFocus.current = true;
      const el = scrollRef.current;
      if (!el) return;
      const top = next * ROW;
      if (top < el.scrollTop) el.scrollTop = top;
      else if (top + ROW > el.scrollTop + el.clientHeight - HEAD) el.scrollTop = top + ROW - el.clientHeight + HEAD;
      setScrollTop(el.scrollTop);
    },
    [rows.length],
  );

  useLayoutEffect(() => {
    if (!pendingFocus.current) return;
    const row = scrollRef.current?.querySelector<HTMLElement>(`[data-row-index="${focusIndex}"]`);
    if (row) {
      row.focus({ preventScroll: true });
      pendingFocus.current = false;
    }
  });

  const entityW = compact ? 158 : 190;
  const avgW = compact ? 44 : 58;
  const cellW = compact ? 58 : 66;
  const columns = `minmax(${entityW}px, 1.7fr) ${avgW}px repeat(${areas.length}, minmax(${cellW}px, 1fr)) 84px`;
  const minWidth = entityW + avgW + areas.length * cellW + 84;
  const compare = new Set(compareIds);
  const highlight = new Set(highlightIds);
  const now = new Date();

  const header = (key: MatrixSortKey, label: React.ReactNode, sub: React.ReactNode, align: 'left' | 'center' = 'center', title?: string) => {
    const on = sort.key === key;
    const ariaSort = on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <div role="columnheader" aria-sort={ariaSort} className={`flex h-full min-w-0 ${align === 'left' ? 'justify-start' : 'justify-center'}`}>
        <button
          type="button"
          title={title}
          onClick={() => onSort({ key, dir: on ? (sort.dir === 'desc' ? 'asc' : 'desc') : key === 'name' ? 'asc' : 'desc' })}
          className={`flex h-full min-w-0 max-w-full flex-col justify-center rounded-lg px-2 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600/40 ${
            align === 'left' ? 'items-start text-left' : 'items-center text-center'
          }`}
        >
          <span className={`flex max-w-full items-center gap-1 text-[12px] ${on ? 'text-ink' : 'text-neutral-500'}`}>
            {label}
            {on && (sort.dir === 'desc' ? <ArrowDown className="h-3 w-3 flex-shrink-0" strokeWidth={2} /> : <ArrowUp className="h-3 w-3 flex-shrink-0" strokeWidth={2} />)}
          </span>
          {sub && <span className="mt-0.5 max-w-full truncate text-[10.5px] text-neutral-400">{sub}</span>}
        </button>
      </div>
    );
  };

  return (
    <div className="absolute inset-0 flex flex-col p-2 sm:p-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-neutral-200/70 px-3 py-2 text-[12px] text-neutral-400 sm:px-4">
          <span className="tabular-nums">
            {rows.length} {rows.length === 1 ? 'entity' : 'entities'}
          </span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-4 rounded-[4px] bg-neutral-200" style={{ backgroundImage: HATCH }} />
              Low confidence
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-neutral-500" />
              AI-assessed
            </span>
            <span className="hidden items-center gap-1.5 sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Older than 12 months
            </span>
          </span>
        </div>

        <div ref={scrollRef} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-auto overscroll-contain" role="table" aria-label="Influence matrix" aria-rowcount={rows.length + 1}>
          <div style={{ minWidth }}>
            <div
              role="row"
              className="sticky top-0 z-20 grid border-b border-neutral-200/70 bg-white/95 backdrop-blur-sm"
              style={{ gridTemplateColumns: columns, height: HEAD }}
            >
              <div className="sticky left-0 z-10 bg-white/95 pl-1.5 sm:pl-2.5">{header('name', 'Entity', null, 'left')}</div>
              {header('average', 'Avg', null, 'center', 'Average across scored areas')}
              {areas.map((area) => {
                const s = summaryById.get(area.id);
                return (
                  <React.Fragment key={area.id}>
                    {header(
                      area.id,
                      <>
                        <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(area.color) }} />
                        <span className="truncate">{area.label}</span>
                      </>,
                      s && s.scored ? `${s.concentration} · ${s.average}` : 'No scores',
                      'center',
                      s ? `${area.label}: ${s.scored} scored · average ${s.average} · ${s.concentration} (HHI ${s.hhi})` : area.label,
                    )}
                  </React.Fragment>
                );
              })}
              {header('network', 'Network', 'percentile', 'center', 'Network position: PageRank percentile over recorded ties')}
            </div>

            <div style={{ height: rows.length * ROW + (compact ? 64 : 0), position: 'relative' }}>
              {rows.length === 0 && <p className="px-4 py-10 text-center text-[13px] text-neutral-400">No matches</p>}
              {slice.map((entity, offset) => {
                const index = start + offset;
                const isSel = entity.id === selectedId;
                const isHover = entity.id === hoverId;
                const net = networkPercentile(analysis, entity.id);
                const metrics = analysis?.metrics.get(entity.id);
                return (
                  <div
                    key={entity.id}
                    role="row"
                    aria-rowindex={index + 2}
                    aria-selected={isSel}
                    data-row-index={index}
                    tabIndex={index === focusIndex ? 0 : -1}
                    onClick={() => onSelect(isSel ? null : entity.id)}
                    onDoubleClick={() => onOpen(entity.id)}
                    onMouseEnter={() => onHover(entity.id)}
                    onMouseLeave={() => onHover(null)}
                    onFocus={() => setFocusIndex(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        moveFocus(index + 1);
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        moveFocus(index - 1);
                      } else if (e.key === 'Home') {
                        e.preventDefault();
                        moveFocus(0);
                      } else if (e.key === 'End') {
                        e.preventDefault();
                        moveFocus(rows.length - 1);
                      } else if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(isSel ? null : entity.id);
                      }
                    }}
                    className={`group absolute inset-x-0 grid cursor-pointer items-center border-b border-neutral-100 outline-none transition-colors focus-visible:bg-neutral-50 ${
                      isSel ? 'bg-primary-50/40' : isHover ? 'bg-neutral-50' : 'bg-white'
                    }`}
                    style={{ gridTemplateColumns: columns, height: ROW, top: index * ROW }}
                  >
                    <div
                      role="cell"
                      className={`sticky left-0 z-10 flex h-full min-w-0 items-center gap-2.5 pl-3 pr-2 transition-colors sm:pl-4 ${
                        isSel ? 'bg-[#fdf6f6]' : isHover ? 'bg-neutral-50' : 'bg-white group-focus-visible:bg-neutral-50'
                      }`}
                    >
                      <span className={`absolute inset-y-1.5 left-0 w-[2px] rounded-r-full transition-colors ${isSel ? 'bg-primary-600' : 'bg-transparent'}`} aria-hidden="true" />
                      <span className="relative">
                        <EntityAvatar entity={entity} size="xs" />
                        {highlight.has(entity.id) && <span className="infl-pulse absolute -inset-1 rounded-full ring-2 ring-primary-600/40" aria-hidden="true" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-[13px] leading-tight ${isSel ? 'text-primary-700' : 'text-ink'}`}>
                          {entity.label}
                          {compare.has(entity.id) && <span className="ml-1.5 align-middle text-[10px] text-neutral-400">compare</span>}
                        </span>
                        <span className="block truncate text-[11.5px] leading-tight text-neutral-400">{entity.subtitle}</span>
                      </span>
                    </div>
                    <div role="cell" className="text-center text-[13px] tabular-nums text-ink">
                      {entity.assessed ? entity.average : <span className="text-[11px] text-neutral-300">—</span>}
                    </div>
                    {areas.map((area) => {
                      const s = entity.scoreByArea[area.id];
                      if (!s) {
                        return (
                          <div key={area.id} role="cell" className="px-1.5 text-center text-[12px] text-neutral-200">
                            ·
                          </div>
                        );
                      }
                      const c = areaColor(area.color);
                      const low = s.confidence != null && s.confidence < LOW_CONFIDENCE;
                      const stale = isStale(s.updatedAt, now);
                      const updated = relativeTime(s.updatedAt, now);
                      const title = [
                        `${area.label} ${s.score}`,
                        s.confidence != null ? `confidence ${s.confidence}%` : null,
                        s.source ? `source ${s.source}` : null,
                        updated ? `updated ${updated}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <div key={area.id} role="cell" className="px-1 sm:px-1.5" title={title}>
                          <span
                            className="relative flex h-7 items-center justify-center rounded-lg text-[12.5px] tabular-nums transition-[background-color] duration-500"
                            style={{
                              backgroundColor: hexAlpha(c, (low ? 0.03 : 0.05) + (low ? 0.22 : 0.42) * (s.score / 100)),
                              backgroundImage: low ? HATCH : undefined,
                              color: s.score >= 60 && !low ? readableOn(c, 0.5) : '#525252',
                              boxShadow: low ? `inset 0 0 0 1px ${hexAlpha(c, 0.35)}` : undefined,
                            }}
                          >
                            {s.score}
                            {s.source === 'ai' && <span className="absolute right-1 top-1 h-1 w-1 rounded-full bg-neutral-500" aria-hidden="true" />}
                            {stale && <span className="absolute bottom-1 right-1 h-1 w-1 rounded-full bg-amber-500" aria-hidden="true" />}
                          </span>
                        </div>
                      );
                    })}
                    <div role="cell" className="flex items-center gap-2 px-2.5" title={metrics ? `${ROLE_LABEL[metrics.role]} · ${metrics.degree} ties` : 'No recorded ties'}>
                      <span className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-100">
                        <span className="block h-full rounded-full bg-neutral-400 transition-[width] duration-500" style={{ width: `${net}%` }} />
                      </span>
                      <span className="w-6 text-right text-[12px] tabular-nums text-neutral-500">{net}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HATCH = 'repeating-linear-gradient(135deg, rgba(255,255,255,0.75) 0 2px, transparent 2px 5px)';
